import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { MessageSquare, Send, CheckCircle2, X, Reply, Building2, MapPin } from "lucide-react";

// Wandelt Snapshot-Sections in eine flache, render-stabile Block-Liste um.
// blockIdx == section_idx im Backend-Schema (Anker beziehen sich darauf).
function flattenSnapshot(sections) {
  const blocks = [];
  let idx = 0;
  const push = (kind, text, extra = {}) => {
    if (!text || !String(text).trim()) return;
    blocks.push({ blockIdx: idx++, kind, text: String(text), ...extra });
  };
  (sections || []).forEach((s) => {
    if (!s) return;
    push("section-name", s.name || "");
    if (s.headline) {
      // Intro hat ggf. 2 Headline-Zeilen (Frage + Überschrift)
      if (s.name === "Intro") {
        const lines = s.headline.split("\n").map((l) => l.trim()).filter(Boolean);
        if (lines.length >= 2) {
          push("headline-question", lines[0]);
          push("headline", lines.slice(1).join(" "));
        } else {
          push("headline", s.headline.trim());
        }
      } else {
        push("headline", s.headline.trim());
      }
    }
    if (s.body) {
      const paragraphs = s.body.split("\n\n").map((p) => p.trim()).filter(Boolean);
      paragraphs.forEach((p, i) => {
        if (s.name === "Intro" && i === 0) {
          // 3-Fragen-Block: jede Frage ein Block
          const raw = p.replace(/^\*\*/, "").replace(/\*\*$/, "");
          raw.split("\n").map((q) => q.trim()).filter(Boolean).forEach((q) => push("question", q));
        } else {
          push("paragraph", p);
        }
      });
    }
    if (s.einleitung) push("paragraph", s.einleitung.trim());
    if (Array.isArray(s.items)) {
      s.items.forEach((it) => push("item", String(it).trim()));
    }
    // Legacy: nur content
    if (!s.headline && !s.body && !s.items && s.content) {
      s.content.split("\n\n").map((p) => p.trim()).filter(Boolean).forEach((p) => push("paragraph", p));
    }
  });
  return blocks;
}

// Zerlegt einen Block-Text in markerfreien Klartext + Formatierungs-Runs.
// **fett** → bold-Run, __unterstrichen__ → underline-Run. Kommentar-Anker beziehen
// sich auf den KLARTEXT (ohne Marker), damit Markierungen offset-stabil bleiben.
function parseFormatting(text) {
  let plain = "";
  const runs = [];
  let bold = false, underline = false, boldStart = 0, underlineStart = 0;
  let i = 0;
  while (i < text.length) {
    if (text[i] === "*" && text[i + 1] === "*") {
      if (!bold) { bold = true; boldStart = plain.length; }
      else { bold = false; if (plain.length > boldStart) runs.push({ start: boldStart, end: plain.length, bold: true }); }
      i += 2; continue;
    }
    if (text[i] === "_" && text[i + 1] === "_") {
      if (!underline) { underline = true; underlineStart = plain.length; }
      else { underline = false; if (plain.length > underlineStart) runs.push({ start: underlineStart, end: plain.length, underline: true }); }
      i += 2; continue;
    }
    plain += text[i]; i += 1;
  }
  return { plain, runs };
}

function getStoredAuthor() {
  try {
    return {
      name: localStorage.getItem("share-author-name") || "",
      role: localStorage.getItem("share-author-role") || "partner",
    };
  } catch {
    return { name: "", role: "partner" };
  }
}

function storeAuthor(name, role) {
  try {
    localStorage.setItem("share-author-name", name);
    localStorage.setItem("share-author-role", role);
  } catch {}
}

function formatTime(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function SharePage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [comments, setComments] = useState([]);
  const [author, setAuthor] = useState(getStoredAuthor);
  const [selection, setSelection] = useState(null); // { blockIdx, start, end, quote, x, y }
  const [composeFor, setComposeFor] = useState(null);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [filter, setFilter] = useState("open"); // open | resolved | all
  const [threadPositions, setThreadPositions] = useState({}); // { [threadId]: topPx }
  const [approvedAt, setApprovedAt] = useState(null);
  const [approvedBy, setApprovedBy] = useState(null);
  const [approveHint, setApproveHint] = useState("");
  const [approveName, setApproveName] = useState("");
  const articleRef = useRef(null);
  const canvasRef = useRef(null);
  const laneRef = useRef(null);
  const approvebarRef = useRef(null);
  const [approvebarH, setApprovebarH] = useState(0);
  const threadRefs = useRef(new Map());
  const viewLoggedRef = useRef(false);
  const seenCommentsRef = useRef(new Set());
  const [scrollTargetId, setScrollTargetId] = useState(null); // nur bei bewusstem Klick auf Highlight/Karte gesetzt → dann scrollt die Karte sichtbar
  const preserveScrollRef = useRef(null); // Scroll-Position, die nach dem Anlegen eines Kommentars erhalten bleibt

  // Daten laden
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/share/${token}`)
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Laden fehlgeschlagen");
        return j;
      })
      .then((j) => {
        if (cancelled) return;
        setData(j);
        setApprovedAt(j.approved_at || null);
        setApprovedBy(j.approved_by || null);
      })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [token]);

  // Höhe der fixierten Freigabe-Leiste messen, damit Inhalt (v. a. der
  // unterste Kommentar samt „Senden") nicht darunter verschwindet.
  useEffect(() => {
    const el = approvebarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setApprovebarH(el.offsetHeight));
    ro.observe(el);
    setApprovebarH(el.offsetHeight);
    return () => ro.disconnect();
  }, [approvedAt, author.name]);

  // Aktivität an das Backend melden (Aufruf / Kommentar angesehen)
  const postEvent = useCallback((type, extra = {}) => {
    fetch(`/api/share/${token}/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type, author_name: author.name || null, author_role: author.role, ...extra }),
    }).catch(() => {});
  }, [token, author]);

  // Aufruf einmal je Laden protokollieren
  useEffect(() => {
    if (!data || viewLoggedRef.current) return;
    viewLoggedRef.current = true;
    postEvent("view");
  }, [data, postEvent]);

  // „Kommentar angesehen“ beim Öffnen eines Threads (einmal je Kommentar)
  useEffect(() => {
    if (!activeThreadId || seenCommentsRef.current.has(activeThreadId)) return;
    seenCommentsRef.current.add(activeThreadId);
    postEvent("comment_seen", { comment_id: activeThreadId });
  }, [activeThreadId, postEvent]);

  const handleApprove = async () => {
    setApproveHint("");
    const name = (author.name || approveName || "").trim();
    if (!name) {
      setApproveHint("Bitte gib zuerst deinen Namen ein.");
      return;
    }
    if (!window.confirm("Diese Stellenanzeige als passend freigeben? Das UNEQ-Team wird darüber informiert.")) return;
    if (!author.name) {
      const role = author.role || "partner";
      setAuthor({ name, role });
      storeAuthor(name, role);
    }
    try {
      const r = await fetch(`/api/share/${token}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ author_name: name }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Freigabe fehlgeschlagen (Status ${r.status})`);
      setApprovedAt(j.approved_at);
      setApprovedBy(j.approved_by);
    } catch (e) {
      setApproveHint(e.message);
    }
  };

  const handleUnapprove = async () => {
    if (!window.confirm("Freigabe wirklich zurücknehmen?")) return;
    setApproveHint("");
    try {
      const r = await fetch(`/api/share/${token}/unapprove`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ author_name: author.name || approvedBy || null }),
      });
      if (!r.ok) throw new Error(`Status ${r.status}`);
      setApprovedAt(null);
      setApprovedBy(null);
    } catch (e) {
      setApproveHint("Zurücknehmen fehlgeschlagen.");
    }
  };

  const loadComments = useCallback(() => {
    fetch(`/api/share/${token}/comments`)
      .then((r) => r.json())
      .then((rows) => { if (Array.isArray(rows)) setComments(rows); })
      .catch(() => {});
  }, [token]);

  useEffect(() => { loadComments(); }, [loadComments]);

  const blocks = useMemo(() => (data ? flattenSnapshot(data.sections) : []), [data]);

  // Kommentare gruppieren: Top-Level → Thread mit Replies
  const threads = useMemo(() => {
    const topLevel = comments.filter((c) => !c.parent_id);
    return topLevel
      .map((t) => ({
        ...t,
        replies: comments.filter((c) => c.parent_id === t.id).sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
      }))
      .sort((a, b) => {
        if (a.section_idx !== b.section_idx) return a.section_idx - b.section_idx;
        return a.anchor_start - b.anchor_start;
      });
  }, [comments]);

  // Anker je Block für Render-Highlight
  const anchorsByBlock = useMemo(() => {
    const map = new Map();
    threads.forEach((t) => {
      if (filter === "open" && t.resolved) return;
      if (filter === "resolved" && !t.resolved) return;
      const list = map.get(t.section_idx) || [];
      list.push({ id: t.id, start: t.anchor_start, end: t.anchor_end });
      map.set(t.section_idx, list);
    });
    return map;
  }, [threads, filter]);

  // Selection-Handler
  const handleMouseUp = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      setSelection(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const anchorEl = range.startContainer.parentElement?.closest?.("[data-block]");
    const focusEl = range.endContainer.parentElement?.closest?.("[data-block]");
    if (!anchorEl || anchorEl !== focusEl) {
      // Selektion über Block-Grenzen → ignorieren
      setSelection(null);
      return;
    }
    const blockIdx = Number(anchorEl.getAttribute("data-block"));
    const blockText = anchorEl.getAttribute("data-block-text") || "";
    // Plain-Text-Range innerhalb des Blocks rekonstruieren
    const preRange = document.createRange();
    preRange.selectNodeContents(anchorEl);
    preRange.setEnd(range.startContainer, range.startOffset);
    const start = preRange.toString().length;
    const fullRange = document.createRange();
    fullRange.selectNodeContents(anchorEl);
    fullRange.setEnd(range.endContainer, range.endOffset);
    const end = fullRange.toString().length;
    if (start === end) { setSelection(null); return; }
    const quote = blockText.slice(start, end);
    if (!quote.trim()) { setSelection(null); return; }
    const rect = range.getBoundingClientRect();
    setSelection({
      blockIdx,
      start: Math.min(start, end),
      end: Math.max(start, end),
      quote,
      x: rect.left + rect.width / 2,
      y: rect.top, // viewport coords (für position: fixed)
    });
  }, []);

  // Bei Klick außerhalb: Selektion-Pin schließen — aber NUR solange noch keine Compose-Box offen ist.
  useEffect(() => {
    if (composeFor) return; // Compose offen → Selektion festhalten
    const onDown = (e) => {
      if (!articleRef.current) return;
      if (e.target.closest(".share-floating-action")) return;
      if (e.target.closest("[data-block]")) return;
      if (e.target.closest(".share-compose, .share-compose-overlay")) return;
      if (window.getSelection()?.isCollapsed) setSelection(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [composeFor]);

  // Submit-Helper (Top-Level oder Reply). Anker kommt aus composeFor (festgehalten beim Öffnen),
  // nicht aus selection — die kann durch Klicks in der Compose-Box bereits weg sein.
  const submitComment = async ({ parentId, body }) => {
    const name = author.name.trim();
    if (!name) return alert("Bitte zuerst Deinen Namen oben rechts eintragen.");
    if (!body.trim()) return;
    let payload;
    if (parentId) {
      payload = { parent_id: parentId, author_name: name, author_role: author.role, body };
    } else {
      const anchor = composeFor;
      if (!anchor) return;
      payload = {
        section_idx: anchor.blockIdx,
        anchor_start: anchor.start,
        anchor_end: anchor.end,
        quoted_text: anchor.quote,
        author_name: name,
        author_role: author.role,
        body,
      };
    }
    const res = await fetch(`/api/share/${token}/comments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) {
      alert(j.error || "Fehler beim Speichern");
      return;
    }
    storeAuthor(name, author.role);
    // Scroll-Position sichern, damit der Re-Render durch den neuen Kommentar
    // die Seite nicht verschiebt. Es wird bewusst KEIN scrollTargetId gesetzt,
    // d.h. die ThreadCard scrollt beim Anlegen nicht (nur bei bewusstem Klick).
    preserveScrollRef.current = window.scrollY;
    setComments((prev) => [...prev, j]);
    if (!parentId) {
      setSelection(null);
      setComposeFor(null);
      window.getSelection()?.removeAllRanges();
      setActiveThreadId(j.id);
    }
  };

  // Threads, gefiltert nach offen/erledigt/alle — wird im Render UND für die Position-Berechnung gebraucht
  const visibleThreads = useMemo(
    () => threads.filter((t) => filter === "all" ? true : filter === "open" ? !t.resolved : t.resolved),
    [threads, filter]
  );

  // Margin-Comments: Y-Position pro Thread berechnen, Kollisionen auflösen
  const recomputePositions = useCallback(() => {
    const canvas = canvasRef.current;
    const article = articleRef.current;
    if (!canvas || !article) return;
    const canvasRect = canvas.getBoundingClientRect();
    // Für jeden sichtbaren Thread: erstes Highlight-DOM-Element finden
    const desired = visibleThreads.map((t) => {
      const mark = article.querySelector(`mark[data-comment-ids~="${t.id}"]`);
      if (!mark) return { id: t.id, top: null };
      const r = mark.getBoundingClientRect();
      return { id: t.id, top: r.top - canvasRect.top };
    });
    // Sortieren nach gewünschter Y-Position
    const sorted = desired
      .filter((d) => d.top !== null)
      .sort((a, b) => a.top - b.top);
    // Kollisionen auflösen: jede Karte mind. 12px unter der vorigen
    const next = {};
    let prevBottom = 0;
    for (const item of sorted) {
      const el = threadRefs.current.get(item.id);
      const h = el ? el.getBoundingClientRect().height : 140;
      const top = Math.max(item.top, prevBottom + 12);
      next[item.id] = top;
      prevBottom = top + h;
    }
    setThreadPositions((prev) => {
      // Nur updaten wenn sich was ändert (verhindert Render-Loops)
      const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
      for (const k of keys) {
        if (Math.abs((prev[k] || 0) - (next[k] || 0)) > 0.5) return next;
      }
      return prev;
    });
  }, [visibleThreads]);

  // Nach dem Anlegen eines Kommentars die gemerkte Scroll-Position synchron
  // (vor dem Paint) wiederherstellen – verhindert jeden Sprung.
  useLayoutEffect(() => {
    if (preserveScrollRef.current == null) return;
    const y = preserveScrollRef.current;
    preserveScrollRef.current = null;
    if (Math.abs(window.scrollY - y) > 1) window.scrollTo(0, y);
  }, [comments]);

  useLayoutEffect(() => {
    recomputePositions();
  }, [recomputePositions, comments, data, activeThreadId]);

  // Re-measure bei Resize, Font-Load oder wenn Karten ihre Größe ändern
  useEffect(() => {
    const onResize = () => recomputePositions();
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(() => recomputePositions());
    if (articleRef.current) ro.observe(articleRef.current);
    threadRefs.current.forEach((el) => { if (el) ro.observe(el); });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => recomputePositions());
    }
    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
    };
  }, [recomputePositions, visibleThreads.length]);

  const toggleResolved = async (threadId, next) => {
    const res = await fetch(`/api/share/${token}/comments/${threadId}/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resolved: next }),
    });
    const j = await res.json().catch(() => ({}));
    if (res.ok) {
      setComments((prev) => prev.map((c) => (c.id === threadId ? { ...c, resolved: j.resolved } : c)));
    }
  };

  if (error) {
    return (
      <div className="share-error-page">
        <h1>Link nicht verfügbar</h1>
        <p>{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="share-loading">Wird geladen …</div>;
  }

  return (
    <div className="share-page" style={{ "--share-approvebar-h": approvebarH ? `${approvebarH}px` : undefined }}>
      <div className="uneq-q-bg" aria-hidden="true" />

      <header className="share-topbar">
        <img src="/uneq-wordmark-light.svg" alt="UNEQ" className="share-topbar-logo" />
        <AuthorBadge author={author} setAuthor={setAuthor} />
      </header>

      <div className="share-canvas" ref={canvasRef}>
        <main className="share-article" ref={articleRef} onMouseUp={handleMouseUp}>
          <div className="share-meta-card">
            <span className="share-meta-eyebrow">Stellenanzeige</span>
            <h1 className="share-meta-title">{data.meta?.stellentitel || "Stellenanzeige"}</h1>
            <span className="share-meta-rule" />
            <div className="share-meta-row">
              {data.meta?.unternehmen && (
                <span><Building2 size={14} /> {data.meta.unternehmen}</span>
              )}
              {data.meta?.standort && (
                <span><MapPin size={14} /> {data.meta.standort}</span>
              )}
            </div>
          </div>

          <div className="share-blocks">
            {blocks.map((b) => (
              <BlockView
                key={b.blockIdx}
                block={b}
                anchors={anchorsByBlock.get(b.blockIdx) || []}
                activeThreadId={activeThreadId}
                onClickHighlight={(id) => { setActiveThreadId(id); setScrollTargetId(id); }}
              />
            ))}
          </div>
        </main>

        <aside className="share-lane" ref={laneRef} aria-label="Kommentare">
          <div className="share-lane-hint">
            <MessageSquare size={14} /> Markiere ein Wort oder einen Satz in der Anzeige, um zu kommentieren.
          </div>
          {threads.length > 0 && (
            <div className="share-lane-head">
              <span className="share-lane-count">{threads.length} Kommentare</span>
              <div className="share-filter-row share-filter-row--top">
                {[
                  { v: "open", l: "Offen" },
                  { v: "resolved", l: "Erledigt" },
                  { v: "all", l: "Alle" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    className={"share-filter-btn" + (filter === o.v ? " active" : "")}
                    onClick={() => setFilter(o.v)}
                  >{o.l}</button>
                ))}
              </div>
            </div>
          )}
          {visibleThreads.map((t) => (
            <div
              key={t.id}
              ref={(el) => {
                if (el) threadRefs.current.set(t.id, el);
                else threadRefs.current.delete(t.id);
              }}
              className="share-pin-slot"
              style={{ top: threadPositions[t.id] ?? 0, opacity: threadPositions[t.id] != null ? 1 : 0 }}
            >
              <ThreadCard
                thread={t}
                active={activeThreadId === t.id}
                shouldScroll={scrollTargetId === t.id}
                onScrolled={() => setScrollTargetId(null)}
                onActivate={() => { setActiveThreadId(t.id); setScrollTargetId(t.id); }}
                onReply={(body) => submitComment({ parentId: t.id, body })}
                onToggleResolved={() => toggleResolved(t.id, !t.resolved)}
                authorReady={!!author.name.trim()}
                bottomInset={approvebarH}
              />
            </div>
          ))}
        </aside>
      </div>

      <div ref={approvebarRef} className={"share-approvebar" + (approvedAt ? " is-approved" : "")}>
        {approvedAt ? (
          <div className="share-approvebar-done">
            <CheckCircle2 size={22} />
            <div className="share-approvebar-done-text">
              <strong>Stellenanzeige freigegeben</strong>
              <span>{approvedBy ? `von ${approvedBy} · ` : ""}{formatTime(approvedAt)} · Danke für deine Rückmeldung!</span>
            </div>
            <button type="button" className="share-approvebar-undo" onClick={handleUnapprove}>
              Freigabe zurücknehmen
            </button>
          </div>
        ) : (
          <>
            <div className="share-approvebar-text">
              <strong>Passt die Stellenanzeige für dich?</strong>
              <span>Markiere Stellen zum Kommentieren – oder gib sie hier frei.</span>
              {approveHint && <span className="share-approve-hint">{approveHint}</span>}
            </div>
            <div className="share-approvebar-action">
              {!author.name && (
                <input
                  type="text"
                  className="share-approvebar-name"
                  placeholder="Dein Name"
                  value={approveName}
                  onChange={(e) => setApproveName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleApprove(); }}
                />
              )}
              <button type="button" className="share-approve-btn" onClick={handleApprove}>
                <CheckCircle2 size={18} /> Stellenanzeige freigeben
              </button>
            </div>
          </>
        )}
      </div>

      {selection && !composeFor && (
        <button
          type="button"
          className="share-floating-action"
          style={{
            left: Math.max(80, Math.min(window.innerWidth - 80, selection.x)),
            top: Math.max(50, selection.y - 12),
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setComposeFor(selection)}
        >
          <MessageSquare size={14} /> Kommentar
        </button>
      )}

      {composeFor && selection && (
        <ComposeBox
          selection={selection}
          author={author}
          setAuthor={setAuthor}
          onCancel={() => { setComposeFor(null); setSelection(null); window.getSelection()?.removeAllRanges(); }}
          onSubmit={(body) => submitComment({ body })}
        />
      )}
    </div>
  );
}

function roleLabel(role) {
  return role === "uneq" ? "UNEQ" : "Partner";
}

function AuthorBadge({ author, setAuthor }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(author.name);
  const [role, setRole] = useState(author.role || "partner");
  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setAuthor({ name: trimmed, role });
    storeAuthor(trimmed, role);
    setEditing(false);
  };
  if (!editing) {
    return (
      <button type="button" className="share-author-badge" onClick={() => setEditing(true)} title="Name ändern">
        <span className={"share-author-dot share-author-dot--" + (author.name ? author.role : "partner")} />
        {author.name ? (
          <>
            <span className="share-author-name">{author.name}</span>
            <span className="share-author-role">{roleLabel(author.role)}</span>
          </>
        ) : (
          <span className="share-author-name">Name festlegen</span>
        )}
      </button>
    );
  }
  return (
    <div className="share-author-edit">
      <input
        autoFocus
        type="text"
        placeholder="Dein Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); }}
      />
      <select value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="partner">Partner</option>
        <option value="uneq">UNEQ</option>
      </select>
      <button type="button" className="share-btn-primary" onClick={save}>Speichern</button>
    </div>
  );
}

function BlockView({ block, anchors, activeThreadId, onClickHighlight }) {
  const { plain, runs } = useMemo(() => parseFormatting(block.text), [block.text]);
  const tag = block.kind === "section-name" ? "h2"
    : block.kind === "headline" ? "h3"
    : block.kind === "headline-question" ? "p"
    : block.kind === "item" ? "li"
    : "p";

  // Bruchpunkte aus Kommentar-Ankern UND Formatierungs-Runs — alle in Klartext-Koordinaten.
  const clamp = (n) => Math.max(0, Math.min(plain.length, n));
  const points = new Set([0, plain.length]);
  anchors.forEach((a) => { points.add(clamp(a.start)); points.add(clamp(a.end)); });
  runs.forEach((r) => { points.add(clamp(r.start)); points.add(clamp(r.end)); });
  const sorted = [...points].sort((a, b) => a - b);

  const inner = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const from = sorted[i], to = sorted[i + 1];
    if (from === to) continue;
    const mid = (from + to) / 2;
    const ids = anchors.filter((a) => a.start <= mid && mid <= a.end).map((a) => a.id);
    const bold = runs.some((r) => r.bold && r.start <= mid && mid <= r.end);
    const underline = runs.some((r) => r.underline && r.start <= mid && mid <= r.end);
    let node = plain.slice(from, to);
    if (bold) node = <strong>{node}</strong>;
    if (underline) node = <u>{node}</u>;
    if (ids.length > 0) {
      const isActive = activeThreadId && ids.includes(activeThreadId);
      inner.push(
        <mark
          key={i}
          className={"share-highlight" + (isActive ? " share-highlight--active" : "")}
          data-comment-ids={ids.join(" ")}
          onClick={(e) => { e.stopPropagation(); onClickHighlight(ids[ids.length - 1]); }}
        >{node}</mark>
      );
    } else {
      inner.push(<React.Fragment key={i}>{node}</React.Fragment>);
    }
  }

  return React.createElement(
    tag,
    {
      className: "share-block share-block--" + block.kind,
      "data-block": block.blockIdx,
      "data-block-text": plain,
    },
    inner
  );
}

function ThreadCard({ thread, active, shouldScroll = false, onScrolled, onActivate, onReply, onToggleResolved, authorReady, bottomInset = 0 }) {
  const [reply, setReply] = useState("");
  const ref = useRef(null);
  // Nur bei BEWUSSTER Aktivierung (Klick auf Highlight/Karte) so scrollen, dass
  // die GANZE Karte (inkl. „Senden") sichtbar ist – nicht hinter der fixierten
  // Freigabe-Leiste unten oder der Topbar oben. Beim Anlegen eines Kommentars
  // wird shouldScroll bewusst NICHT gesetzt, damit die Seite stehen bleibt.
  // scrollIntoView({block:"nearest"}) kennt die fixierten Leisten nicht, daher manuell.
  useLayoutEffect(() => {
    if (!shouldScroll || !ref.current) return;
    const topInset = 64; // Höhe der Sticky-Topbar
    const gap = 12;
    const rect = ref.current.getBoundingClientRect();
    const bottomLimit = window.innerHeight - bottomInset - gap;
    let delta = 0;
    if (rect.bottom > bottomLimit) {
      delta = rect.bottom - bottomLimit; // nach unten scrollen, bis „Senden" frei liegt
    } else if (rect.top < topInset + gap) {
      delta = rect.top - topInset - gap; // nach oben scrollen, falls verdeckt
    }
    if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: "smooth" });
    onScrolled?.();
  }, [shouldScroll, bottomInset, onScrolled]);
  const submit = () => {
    if (!reply.trim()) return;
    if (!authorReady) { alert("Bitte zuerst Namen oben rechts eintragen."); return; }
    onReply(reply.trim());
    setReply("");
  };
  return (
    <div ref={ref} className={"share-thread" + (active ? " share-thread--active" : "") + (thread.resolved ? " share-thread--resolved" : "")} onClick={onActivate}>
      <div className="share-thread-quote">„{thread.quoted_text}"</div>
      <CommentRow c={thread} />
      {thread.replies.map((r) => <CommentRow key={r.id} c={r} reply />)}
      <div className="share-thread-actions">
        <textarea
          rows={2}
          placeholder="Antworten …"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
        />
        <div className="share-thread-actions-row">
          <button type="button" className="share-btn-ghost" onClick={onToggleResolved}>
            <CheckCircle2 size={14} /> {thread.resolved ? "Wieder öffnen" : "Erledigt"}
          </button>
          <button type="button" className="share-btn-primary" onClick={submit} disabled={!reply.trim()}>
            <Send size={13} /> Senden
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentRow({ c, reply }) {
  return (
    <div className={"share-comment" + (reply ? " share-comment--reply" : "")}>
      <div className="share-comment-head">
        <span className={"share-author-dot share-author-dot--" + c.author_role} />
        <span className="share-comment-author">{c.author_name}</span>
        <span className="share-comment-role">{roleLabel(c.author_role)}</span>
        <span className="share-comment-time">{formatTime(c.created_at)}</span>
      </div>
      <div className="share-comment-body">{c.body}</div>
    </div>
  );
}

function ComposeBox({ selection, author, setAuthor, onCancel, onSubmit }) {
  const [body, setBody] = useState("");
  const [name, setName] = useState(author.name);
  const [role, setRole] = useState(author.role || "partner");
  const submit = () => {
    if (!body.trim()) return;
    const trimmedName = name.trim();
    if (!trimmedName) { alert("Bitte Namen eintragen"); return; }
    if (trimmedName !== author.name || role !== author.role) {
      setAuthor({ name: trimmedName, role });
      storeAuthor(trimmedName, role);
    }
    onSubmit(body.trim());
    setBody("");
  };
  return (
    <div className="share-compose-overlay" onClick={(e) => { if (e.target.classList.contains("share-compose-overlay")) onCancel(); }}>
      <div className="share-compose">
        <div className="share-compose-head">
          <span className="share-compose-title">Neuer Kommentar</span>
          <button type="button" className="share-icon-btn" onClick={onCancel}><X size={14} /></button>
        </div>
        <div className="share-compose-quote">„{selection.quote}"</div>
        {!author.name && (
          <div className="share-compose-author-row">
            <input
              type="text"
              placeholder="Dein Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="partner">Partner</option>
              <option value="uneq">UNEQ</option>
            </select>
          </div>
        )}
        <textarea
          rows={4}
          placeholder="Dein Kommentar …"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          autoFocus={!!author.name}
        />
        <div className="share-compose-foot">
          <span className="share-compose-hint">⌘/Ctrl + Enter</span>
          <button type="button" className="share-btn-primary" onClick={submit} disabled={!body.trim()}>
            <Send size={13} /> Kommentar senden
          </button>
        </div>
      </div>
    </div>
  );
}
